import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import {
  ConsumerSearchFilters,
  getConsumerBillFilters,
  searchConsumerwithExtraInfoFilters,
} from "./types";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { callCenterDashboardApi } from "./api";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useToast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { downloadFile } from "@shared/utils/utils";

export const useConsumerSearch = (filters: ConsumerSearchFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.callCenter.consumerSearch(),
    () => callCenterDashboardApi.searchConsumer(filters),
    {
      enabled: filters.searchData.length >= 3,
    }
  );
};

export const useConsumerSearchWithExtraInfo = (
  filters: searchConsumerwithExtraInfoFilters
) => {
  return useSmartQuery(
    QueryKeyFactory.module.callCenter.consumerSearchWithExtraInfo(
      filters.searchData
    ),
    () => callCenterDashboardApi.searchConsumerwithExtraInfo(filters),
    {
      enabled: filters.searchData.length >= 3,
    }
  );
};

export const useCallPurpose = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.callCenter.callPurpose(),
    () => callCenterDashboardApi.getCallPurpose(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useCallStatus = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.callCenter.callStatus(),
    () => callCenterDashboardApi.getCallStatus(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useCreateCallEntry = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => callCenterDashboardApi.createCallEntry(payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["callCenter", "summaryDashboard", "list"],
        });
        toast({
          title: "Call entry created successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.callCenter.allCallEntries(),
        });
      },
      onError: () => {
        toast({ title: "Call entry creation failed!" });
      },
    }
  );
};

export const useSummaryDashboard = (createdBy: any) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.callCenter.summaryDashboard(),
    () =>
      callCenterDashboardApi.getSummaryDashboard(remoteUtilityId, createdBy),
    {
      enabled: !!remoteUtilityId && !!createdBy,
    }
  );
};

export const useAllCallEntries = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.callCenter.allCallEntries(),
    () => callCenterDashboardApi.getAllCallEntries(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetConsumerBill = (filters: getConsumerBillFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.callCenter.getConsumerBill(filters.consumerNumber),
    () => callCenterDashboardApi.getConsumerBill(filters),
    {
      enabled: !!filters.consumerNumber,
    }
  );
};

export const useDownloadBill = (billId: string) => {
  return useSmartMutation(
    async ({
      billId,
      filename = "template.pdf",
    }: {
      billId: string;
      filename?: string;
    }) => {
      const blob = await callCenterDashboardApi.downloadBill(billId);
      downloadFile(blob, filename);
      return blob;
    }
  );
};
